import { useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isSessionExpiringSoon, extendSession } from '../utils/auth';

export const useSession = () => {
  const { user, token } = useAuth();

  // Extend session on user activity
  const handleUserActivity = useCallback(() => {
    if (user && token) {
      extendSession();
    }
  }, [user, token]);

  // Check for session expiration warnings
  useEffect(() => {
    if (!user || !token) return;

    const checkSessionExpiration = () => {
      if (isSessionExpiringSoon()) {
        // Show warning to user about session expiring
        console.warn('Session will expire soon. Please save your work.');
        // You can add a toast notification here if you want
      }
    };

    // Check every 5 minutes
    const interval = setInterval(checkSessionExpiration, 5 * 60 * 1000);
    
    // Initial check
    checkSessionExpiration();

    return () => clearInterval(interval);
  }, [user, token]);

  // Add event listeners for user activity
  useEffect(() => {
    if (!user || !token) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const activityHandler = () => {
      handleUserActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, activityHandler, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, activityHandler);
      });
    };
  }, [user, token, handleUserActivity]);

  // Extend session on page visibility change (when user returns to tab)
  useEffect(() => {
    if (!user || !token) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleUserActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, token, handleUserActivity]);

  return {
    isActive: !!user && !!token,
    extendSession: handleUserActivity
  };
}; 